// House jobs: tasks with `every_days`, done now and then rather than on a day.
// `isStale` mirrors reconcile.IsStale in Go (keep them in step): an open job is
// stale from (done_at, else the day it was made) + every_days on, unless a day it
// was deferred to is still ahead. Nothing here counts anything.
import { addDays, dateOf } from '@/lib/time'
import type { TaskResource } from '@/kinds/task/apply'

const JOB_STATES = ['inbox', 'next', 'waiting']

export const isJob = (t: TaskResource) => !!t.spec.every_days && !t.spec.recurrence

/** The day a job's clock counts from. */
export const jobBaseline = (t: TaskResource): string => t.status.done_at ?? dateOf(new Date(t.created_at))

export const isStale = (t: TaskResource, today: string): boolean => {
  if (!isJob(t) || !JOB_STATES.includes(t.status.state)) return false
  if (t.spec.scheduled && t.spec.scheduled > today) return false
  return today >= addDays(jobBaseline(t), t.spec.every_days!)
}

const dueOf = (t: TaskResource) => addDays(jobBaseline(t), t.spec.every_days!)

/**
 * Every open job, the soonest due first (so the stale ones lead), for the Home
 * widget: Done is there before the nudge as well as after it.
 */
export const openJobs = (tasks: TaskResource[]): TaskResource[] =>
  tasks
    .filter((t) => !t.deleted_at && isJob(t) && JOB_STATES.includes(t.status.state))
    .sort((a, b) => {
      const da = dueOf(a)
      const db = dueOf(b)
      return da < db ? -1 : da > db ? 1 : a.id < b.id ? -1 : 1
    })

/** The jobs that are due, the longest-waiting first. */
export const staleJobs = (tasks: TaskResource[], today: string): TaskResource[] =>
  tasks
    .filter((t) => !t.deleted_at && isStale(t, today))
    .sort((a, b) => {
      const da = addDays(jobBaseline(a), a.spec.every_days!)
      const db = addDays(jobBaseline(b), b.spec.every_days!)
      return da < db ? -1 : da > db ? 1 : a.id < b.id ? -1 : 1
    })
